/**
 * Pantalla **supervisor de Talonarios** (principal). Reemplaza el placeholder de
 * `ROUTES.talonarios`. Equivale a `supervisor_talonarios.html` del SSR.
 *
 * Exige `manage_talonarios` (mismo permiso que la API y el menú del SSR), vía
 * `RequireManageTalonarios`. Orquesta:
 *  - el estado de todos los talonarios (`useTalonarios`) en una tabla
 *    (`TalonariosTable`) con worker/rango/ocupados/disponibles/% uso/estado;
 *  - el panel de alertas de salto (`TalonarioAlertasPanel`, polling de `/alertas`);
 *  - la asignación de un nuevo rango (`AssignTalonarioDialog`, modal con Zod);
 *  - accesos a Historial y Descuadres.
 *
 * Presentacional + hooks; sin `fetch` (la sede viaja por `X-Sede-Id` en lib/api).
 * Estados de carga/error siempre contemplados.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { Button, Card, CardContent } from "@/components/ui";
import { ROUTES } from "@/features/navigation";

import { talonarioErrorMessage } from "../format";
import { useTalonarios } from "../hooks/useTalonarios";
import { AssignTalonarioDialog } from "./AssignTalonarioDialog";
import { RequireManageTalonarios } from "./RequireManageTalonarios";
import { TalonarioAlertasPanel } from "./TalonarioAlertasPanel";
import { TalonariosTable } from "./TalonariosTable";

/** Trabajador (+ rango) cuyo modal de asignación está abierto. */
interface AssignTarget {
  worker: string;
  currentRange: string;
}

export function TalonariosScreen() {
  const talonarios = useTalonarios();
  const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null);

  return (
    <RequireManageTalonarios title="Talonarios">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Talonarios</h1>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="secondary" size="md">
              <Link to={ROUTES.talonarioHistory}>Historial</Link>
            </Button>
            <Button asChild variant="secondary" size="md">
              <Link to={ROUTES.talonarioMismatches}>Descuadres</Link>
            </Button>
          </div>
        </div>

        <TalonarioAlertasPanel />

        <Card>
          <CardContent className="p-0">
            {talonarios.isPending ? (
              <p className="px-1 py-8 text-center text-sm text-slate-500" role="status">
                Cargando talonarios…
              </p>
            ) : talonarios.isError ? (
              <p className="px-1 py-8 text-center text-sm text-red-600" role="alert">
                {talonarioErrorMessage(talonarios.error)}
              </p>
            ) : (
              <TalonariosTable
                items={talonarios.data.items}
                onAssign={(worker, currentRange) =>
                  setAssignTarget({ worker, currentRange })
                }
              />
            )}
          </CardContent>
        </Card>

        <AssignTalonarioDialog
          worker={assignTarget?.worker ?? null}
          currentRange={assignTarget?.currentRange}
          onClose={() => setAssignTarget(null)}
        />
      </div>
    </RequireManageTalonarios>
  );
}
